import React, { useState } from "react";
import { toast } from "sonner";
import { MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/i18n";
import { useFormatEUR } from "@/lib/useFormatEUR";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

/** Buitenreparatie: callout fee + km (end − start) × km-rate + on-site hours. */
export default function OnsitePanel({ card, setCard, settings, locked }) {
  const { t } = useLang();
  const fm = useFormatEUR();
  const [start, setStart] = useState(card.onsite_km_start ?? "");
  const [end, setEnd] = useState(card.onsite_km_end ?? "");
  const [busy, setBusy] = useState(false);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/repairs/${card.id}`, patch);
      setCard(data);
    } catch { toast.error(t("saveFailed")); }
    finally { setBusy(false); }
  };

  const kmTotal = start !== "" && end !== "" ? Math.max(0, Number(end) - Number(start)) : 0;

  return (
    <Card className="p-5 space-y-4" data-testid="onsite-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-amber-500" />
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{t("onsiteRepair")}</div>
        </div>
        <Switch checked={!!card.onsite} disabled={locked || busy} onCheckedChange={(v) => save({ onsite: v })} data-testid="onsite-toggle" />
      </div>
      {card.onsite && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">{t("kmStart")}</div>
              <Input type="number" min="0" dir="ltr" value={start} disabled={locked} onChange={(e) => setStart(e.target.value)} data-testid="onsite-km-start" />
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">{t("kmEnd")}</div>
              <Input type="number" min="0" dir="ltr" value={end} disabled={locked} onChange={(e) => setEnd(e.target.value)} data-testid="onsite-km-end" />
            </div>
            <div className="flex items-end">
              <Button variant="outline" className="w-full rounded-full" disabled={locked || busy}
                onClick={() => save({ onsite_km_start: start === "" ? 0 : Number(start), onsite_km_end: end === "" ? 0 : Number(end) })}
                data-testid="onsite-km-save">{t("calculateKm")}</Button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
            {[
              [t("calloutFee"), fm(card.onsite_callout || 0), "onsite-callout"],
              [`${t("kilometers")} · ${kmTotal || card.onsite_km_total || 0} km`, fm(card.onsite_km_charge || 0), "onsite-km-charge"],
              [t("onsiteWork"), fm(card.onsite_labor_charge || 0), "onsite-labor"],
              [t("onsiteTotal"), fm(card.onsite_total || 0), "onsite-total"],
            ].map(([label, val, tid]) => (
              <div key={tid} className="p-3 rounded-md border border-border">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
                <div className="font-mono font-bold text-lg tabular-nums" data-testid={tid}>{val}</div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground">
            {t("onsiteHint", { fee: fm(settings?.callout_fee || 0), km: fm(settings?.km_rate || 0), rate: fm(settings?.labor_rate_onsite || settings?.labor_rate || 0) })}
          </p>
        </>
      )}
    </Card>
  );
}
